import {announce} from '@github-ui/aria-live'
import {useDebounce} from '@github-ui/use-debounce'
import pluralize from 'pluralize'
import {useCallback, useEffect, useEffectEvent, useRef} from 'react'

const ANNOUNCE_DELAY_MS = 150

interface SearchAnnouncementStatus {
  /** True while search results are stale because a request is in flight. */
  pending?: boolean
  failed?: boolean
}

export function useSidebarSearchAnnouncement(
  filter: string,
  resultCount: number,
  noun: string,
  {pending = false, failed = false}: SearchAnnouncementStatus = {},
) {
  const announcementElementRef = useRef<HTMLElement>(null)
  const announceSearchResults = useDebounce(
    (query: string, element: HTMLElement, hasFailed: boolean) => {
      if (!element.isConnected) return

      if (hasFailed) {
        announce(`Could not load ${pluralize(noun)}`, {element})
        return
      }

      const label = pluralize(noun, resultCount)
      announce(query ? `${resultCount} matching ${label}` : `${resultCount} ${label} available`, {element})
    },
    ANNOUNCE_DELAY_MS,
    {onChangeBehavior: 'cancel'},
  )

  const announceCurrentSearchResults = useEffectEvent(() => {
    if (pending) {
      // A later settled state will announce; anything queued now would report a stale count.
      announceSearchResults.cancel()
      return
    }
    if (announcementElementRef.current) {
      announceSearchResults(filter, announcementElementRef.current, failed)
    }
  })

  useEffect(() => {
    announceCurrentSearchResults()
  }, [filter, resultCount, pending, failed])

  return useCallback((element: HTMLElement) => {
    announcementElementRef.current = element
  }, [])
}
